import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import type { MouseEvent } from 'react';
import {
  BillingContext,
  UsagePeriodKind,
  UsagePeriodValue,
  canUseBillingPeriod,
  formatPeriodLabel,
  resolvePeriod,
  shiftPeriod,
  toApiDate,
} from './usagePeriod';

type Props = {
  value: UsagePeriodValue;
  onChange: (next: UsagePeriodValue) => void;
  /** When set (tenant 360), shows “Számlázási” preset. */
  billing?: BillingContext | null;
  disabled?: boolean;
  size?: 'small' | 'medium';
};

const KIND_OPTIONS: { kind: UsagePeriodKind; label: string }[] = [
  { kind: 'day', label: 'Napi' },
  { kind: 'week', label: 'Heti' },
  { kind: 'month', label: 'Havi' },
  { kind: 'custom', label: 'Egyedi' },
];

const UsagePeriodPicker = ({
  value,
  onChange,
  billing = null,
  disabled = false,
  size = 'small',
}: Props) => {
  const showBilling = canUseBillingPeriod(billing);
  const isCustom = value.kind === 'custom';

  const handleKind = (
    _e: MouseEvent<HTMLElement>,
    kind: UsagePeriodKind | null
  ) => {
    if (!kind || kind === value.kind) return;
    if (kind === 'custom') {
      onChange(
        resolvePeriod('custom', {
          customFrom: value.from,
          customTo: value.to,
        })
      );
      return;
    }
    if (kind === 'billing') {
      onChange(resolvePeriod('billing', { billing, anchor: value.from }));
      return;
    }
    onChange(resolvePeriod(kind, { anchor: value.from }));
  };

  const handleShift = (direction: -1 | 1) => {
    onChange(shiftPeriod(value, direction, billing));
  };

  const handleCustomFrom = (next: Dayjs | null) => {
    if (!next || !next.isValid()) return;
    onChange(
      resolvePeriod('custom', {
        customFrom: toApiDate(next),
        customTo: value.to,
      })
    );
  };

  const handleCustomTo = (next: Dayjs | null) => {
    if (!next || !next.isValid()) return;
    onChange(
      resolvePeriod('custom', {
        customFrom: value.from,
        customTo: toApiDate(next),
      })
    );
  };

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        sx={{ alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', flexShrink: 0 }}
        >
          <Tooltip title="Előző időszak">
            <span>
              <IconButton
                size={size}
                onClick={() => handleShift(-1)}
                disabled={disabled}
                aria-label="Előző időszak"
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <ToggleButtonGroup
            exclusive
            size={size}
            value={value.kind}
            onChange={handleKind}
            disabled={disabled}
            sx={{
              flexWrap: 'wrap',
              '& .MuiToggleButton-root': {
                px: 1.25,
                py: 0.5,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px !important',
                border: '1px solid',
                borderColor: 'divider',
                mx: 0.25,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                },
              },
            }}
          >
            {KIND_OPTIONS.map((opt) => (
              <ToggleButton key={opt.kind} value={opt.kind}>
                {opt.label}
              </ToggleButton>
            ))}
            {showBilling && (
              <ToggleButton value="billing">Számlázási</ToggleButton>
            )}
          </ToggleButtonGroup>

          <Tooltip title="Következő időszak">
            <span>
              <IconButton
                size={size}
                onClick={() => handleShift(1)}
                disabled={disabled}
                aria-label="Következő időszak"
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontWeight: 600,
            letterSpacing: 0.2,
            minWidth: 0,
            flex: 1,
          }}
        >
          {formatPeriodLabel(value)}
          {value.kind === 'billing' && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1, fontWeight: 400 }}
            >
              (számlázási ciklus)
            </Typography>
          )}
        </Typography>
      </Stack>

      <Collapse in={isCustom}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <DatePicker
            label="Kezdő dátum"
            value={dayjs(value.from)}
            onChange={handleCustomFrom}
            disabled={disabled}
            format="YYYY-MM-DD"
            slotProps={{
              textField: { size: 'small', fullWidth: true },
            }}
          />
          <DatePicker
            label="Záró dátum"
            value={dayjs(value.to)}
            onChange={handleCustomTo}
            disabled={disabled}
            format="YYYY-MM-DD"
            slotProps={{
              textField: { size: 'small', fullWidth: true },
            }}
          />
        </Stack>
      </Collapse>
    </Box>
  );
};

export default UsagePeriodPicker;
